import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import api, { API, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import { ArrowLeft, Download, Upload, FileCheck2, ExternalLink, CheckCircle2, XCircle } from "lucide-react";

const DEFAULT_CHECKLIST = [
  "Clean cane storage",
  "Filtered juice",
  "No artificial colouring",
  "Wood-fired / traditional process documented",
  "Food-grade packaging",
  "Batch tested for moisture < 5%",
  "Worker hygiene SOP followed",
];

function StatusPill({ s }) {
  const map = {
    verified: "bg-primary/10 text-primary border-primary/30",
    pending: "bg-secondary/10 text-secondary border-secondary/30",
    rejected: "bg-destructive/10 text-destructive border-destructive/30",
  };
  return <Badge variant="outline" className={`${map[s] || ""} capitalize`}>{s}</Badge>;
}

export default function BatchDetails() {
  const { batchId } = useParams();
  const [batch, setBatch] = useState(null);
  const [checklist, setChecklist] = useState([]);
  const [loading, setLoading] = useState(true);
  const qrRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/batches/${batchId}`);
      setBatch(data);
      const cl = (data.sop_checklist && data.sop_checklist.length)
        ? data.sop_checklist
        : DEFAULT_CHECKLIST.map(l => ({ label: l, checked: false }));
      setChecklist(cl);
    } catch { toast.error("Could not load batch"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [batchId]);

  const verifyUrl = `${window.location.origin}/verify/${batchId}`;

  const upload = async (kind, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("kind", kind);
    fd.append("file", file);
    try {
      await api.post(`/batches/${batchId}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${kind === "lab_report" ? "Lab report" : "SOP report"} uploaded`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
  };

  const saveChecklist = async () => {
    try {
      await api.post(`/batches/${batchId}/sop-checklist`, checklist);
      toast.success("Checklist saved");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
  };

  const downloadQr = () => {
    const canvas = qrRef.current?.querySelector("canvas");
    if (!canvas) return;
    const link = document.createElement("a");
    link.download = `gudlink-${batch.batch_code}-qr.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
  };

  if (loading || !batch) return <div className="max-w-7xl mx-auto px-6 py-10 text-muted-foreground">Loading…</div>;

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8">
      <Link to="/fpo" className="text-sm text-muted-foreground inline-flex items-center hover:text-foreground" data-testid="back-btn">
        <ArrowLeft className="h-4 w-4 mr-1"/>Back to dashboard
      </Link>

      <div className="mt-4 grid lg:grid-cols-3 gap-6">
        {/* Left: batch info + uploads */}
        <div className="lg:col-span-2 space-y-6">
          <div className="rounded-2xl bg-card border border-border p-6">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-secondary">{batch.product_type}</div>
                <h1 className="font-marketing text-4xl mt-1">{batch.batch_code}</h1>
                <p className="text-muted-foreground text-sm mt-1">{batch.fpo_org} · {batch.source_location}</p>
              </div>
              <StatusPill s={batch.status}/>
            </div>
            <div className="grid sm:grid-cols-3 gap-4 mt-6 text-sm">
              <div><div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Quantity</div><div className="mt-1 font-medium">{batch.quantity_kg} kg</div></div>
              <div><div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Harvest</div><div className="mt-1 font-medium">{batch.harvest_date}</div></div>
              <div><div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Processing</div><div className="mt-1 font-medium">{batch.processing_date}</div></div>
              <div><div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Scans</div><div className="mt-1 font-medium">{batch.scans_count}</div></div>
              <div className="sm:col-span-2"><div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Notes</div><div className="mt-1">{batch.notes || "—"}</div></div>
            </div>
            {batch.admin_note && (
              <div className="mt-4 rounded-lg bg-muted p-3 text-sm"><b>Admin note:</b> {batch.admin_note}</div>
            )}
          </div>

          <div className="rounded-2xl bg-card border border-border p-6">
            <h2 className="font-heading text-lg font-semibold">Documents</h2>
            <p className="text-sm text-muted-foreground">Attach 3rd-party lab report and your SOP/hygiene document.</p>
            <div className="grid sm:grid-cols-2 gap-4 mt-4">
              {["lab_report", "sop_report"].map(k => {
                const file = batch[k];
                return (
                  <div key={k} className="rounded-xl border border-border p-4 bg-muted/40">
                    <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{k === "lab_report" ? "Lab report" : "SOP report"}</div>
                    {file ? (
                      <>
                        <div className="mt-2 flex items-center gap-2 text-sm">
                          <FileCheck2 className="h-4 w-4 text-primary"/>
                          <span className="truncate">{file.original_filename}</span>
                        </div>
                        <a
                          href={`${API}/files?path=${encodeURIComponent(file.storage_path)}`}
                          target="_blank" rel="noreferrer"
                          className="mt-2 inline-flex items-center text-sm text-primary hover:underline"
                          data-testid={`view-${k}-link`}
                        >
                          View <ExternalLink className="h-3.5 w-3.5 ml-1"/>
                        </a>
                      </>
                    ) : (
                      <div className="mt-2 text-sm text-muted-foreground">No file uploaded yet</div>
                    )}
                    <label className="mt-3 inline-flex items-center gap-2 cursor-pointer text-sm rounded-full border border-primary/40 text-primary px-3 py-1 hover:bg-primary/5">
                      <Upload className="h-3.5 w-3.5"/>{file ? "Replace" : "Upload"}
                      <input type="file" accept=".pdf,image/*" className="hidden" onChange={e=>upload(k, e.target.files[0])} data-testid={`upload-${k}-input`}/>
                    </label>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="rounded-2xl bg-card border border-border p-6">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h2 className="font-heading text-lg font-semibold">SOP / Hygiene checklist</h2>
                <p className="text-sm text-muted-foreground">Confirm the practices followed for this batch.</p>
              </div>
              <Button onClick={saveChecklist} className="bg-primary hover:bg-primary/90" data-testid="save-checklist-btn">Save</Button>
            </div>
            <div className="mt-4 grid sm:grid-cols-2 gap-2">
              {checklist.map((c, i) => (
                <label key={i} className="flex items-start gap-3 rounded-lg border border-border/70 px-3 py-2">
                  <Checkbox checked={c.checked} onCheckedChange={(v)=> setChecklist(list => list.map((x,idx)=> idx===i ? {...x, checked: !!v} : x))} data-testid={`checklist-${i}`}/>
                  <span className="text-sm">{c.label}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Right: QR + verify */}
        <aside className="space-y-6">
          <div className="rounded-2xl bg-card border border-border p-6">
            <h2 className="font-heading text-lg font-semibold">Batch QR</h2>
            <p className="text-sm text-muted-foreground">Print this QR on your packaging. Any buyer can scan it to verify the batch.</p>
            <div ref={qrRef} className="mt-4 grid place-items-center bg-white p-4 rounded-xl border border-border">
              <QRCodeCanvas value={verifyUrl} size={200} bgColor="#FFFFFF" fgColor="#1B3B22" level="H" includeMargin/>
            </div>
            <div className="mt-3 text-xs text-muted-foreground break-all">{verifyUrl}</div>
            <div className="mt-4 flex gap-2">
              <Button onClick={downloadQr} className="bg-primary hover:bg-primary/90 flex-1" data-testid="download-qr-btn">
                <Download className="h-4 w-4 mr-2"/>Download PNG
              </Button>
              <Button asChild variant="outline" className="flex-1" data-testid="open-verify-btn">
                <a href={verifyUrl} target="_blank" rel="noreferrer">Open</a>
              </Button>
            </div>
          </div>

          <div className="rounded-2xl bg-muted/60 border border-border p-6">
            <h3 className="font-heading font-semibold flex items-center gap-2">
              {batch.status === "verified"
                ? <><CheckCircle2 className="h-5 w-5 text-primary"/>Verified by Gudlink</>
                : batch.status === "rejected"
                ? <><XCircle className="h-5 w-5 text-destructive"/>Not verified</>
                : <>Awaiting admin review</>}
            </h3>
            <p className="text-sm text-muted-foreground mt-2">Once verified, the buyer verification page will show a bold green "Verified" badge for this batch.</p>
          </div>
        </aside>
      </div>
    </div>
  );
}
